import type { LoadedBlock } from '@/features/shared/services/block-loader'

export interface ActiveBlockState {
  block: LoadedBlock
  ref: { category: string; blockId: string }
}

export interface BlockSlice {
  activeBlock: ActiveBlockState | null
  /**
   * Convenience accessor — `activeBlock?.ref?.blockId ?? ''`. Empty string when
   * no block is active so consumers can treat empty-string as "no current block"
   * without null-checking.
   */
  currentBlockId: string
  setActiveBlock: (block: LoadedBlock, ref: { category: string; blockId: string }) => void
  clearActiveBlock: () => void
  /**
   * Replace the `ui` payload of the currently active block. Used by the
   * brainstorm overlay's apply path to swap in a candidate's cogoJson. No-op
   * when `id` doesn't match the active block's blockId (defensive — the active
   * block may have changed between drawer open and pick).
   */
  replaceBlock: (id: string, cogoJson: unknown) => void
}

type Set = (fn: (state: BlockSlice) => void) => void

export function createBlockSlice(
  set: Set,
  _get: unknown,
  _api: unknown,
): BlockSlice {
  return {
    activeBlock: null,
    currentBlockId: '',
    setActiveBlock: (block, ref) =>
      set((s) => {
        s.activeBlock = { block, ref }
        s.currentBlockId = ref.blockId
      }),
    clearActiveBlock: () =>
      set((s) => {
        s.activeBlock = null
        s.currentBlockId = ''
      }),
    replaceBlock: (id, cogoJson) =>
      set((s) => {
        if (!s.activeBlock || s.activeBlock.ref.blockId !== id) return
        // Swap only the `ui` field — keep meta/events/flow/state/data so the
        // surrounding 6-file block keeps its identity. cogoJson shape mirrors
        // ui.cogo.json (root component tree).
        s.activeBlock.block = {
          ...s.activeBlock.block,
          ui: cogoJson as typeof s.activeBlock.block.ui,
        }
      }),
  }
}
